import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, ChevronRight, BookOpen,
  Star, Flame, MessageCircle, Heart, Pencil,
  CalendarDays, Target, TrendingUp, Sparkles, Lock, Clock,
  DollarSign, CheckCircle2, ArrowRight, X, Save,
  GraduationCap, Moon, Users, Crown
} from 'lucide-react';
import confetti from 'canvas-confetti';
import { useAuth } from '../contexts/AuthContext';
import { useMentorSignupEnabled } from '../hooks/useMentorSignupEnabled';
import AdSlot from './AdSlot';
import * as api from '../lib/api';
import { defaultAvatar } from '../lib/avatarDefaults';
import posthog from '../lib/posthog';
import { growthFocusLabel } from '../lib/onboardingContent';

const badgeIconMap = {
  'message-circle': MessageCircle,
  'flame': Flame,
  'graduation-cap': GraduationCap,
  'book-open': BookOpen,
  'star': Star,
  'moon': Moon,
  'users': Users,
  'heart': Heart,
  'crown': Crown,
};

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

const PRAYER_NAMES = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];
const PRAYER_LABELS = { fajr: 'Fajr', dhuhr: 'Dhuhr', asr: 'Asr', maghrib: 'Maghrib', isha: 'Isha' };

/* ─── Financial Thermometer ─── */
const FinancialThermometer = ({ goalType, current, target, onEdit }) => {
  const isSavings = goalType === 'Build my savings';
  const label = isSavings ? 'Savings' : 'Debt Payoff';
  const currentNum = parseFloat(current) || 0;
  const targetNum = parseFloat(target) || 1;

  // progressPct: how far toward the goal (0–100), shown as the label
  // fillPct: how much of the bar is filled visually
  let progressPct, fillPct;
  if (isSavings) {
    progressPct = Math.min(100, Math.max(0, Math.round((currentNum / targetNum) * 100)));
    fillPct = progressPct; // bar grows upward from bottom
  } else {
    // target = initial total debt; current = remaining debt
    // progressPct = how much has been paid off
    progressPct = Math.min(100, Math.max(0, Math.round(((targetNum - currentNum) / targetNum) * 100)));
    // fillPct = how much debt remains (bar drains from top downward)
    fillPct = Math.min(100, Math.max(0, Math.round((currentNum / targetNum) * 100)));
  }

  const fillColor = progressPct >= 75 ? '#22c55e' : progressPct >= 40 ? '#eab308' : '#4AADA8';

  return (
    <div data-testid="financial-thermometer" className="relative group">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-text-main flex items-center gap-1.5">
          <DollarSign className="w-3.5 h-3.5 text-primary" />
          {label} Progress
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold" style={{ color: fillColor }}>{progressPct}%</span>
          {onEdit && (
            <button onClick={onEdit}
              className="p-1 rounded-full opacity-0 group-hover:opacity-100 hover:bg-primary/10 transition-all"
              data-testid="edit-financial-btn">
              <Pencil className="w-3 h-3 text-text-muted" />
            </button>
          )}
        </div>
      </div>

      <div className="relative h-32 w-10 mx-auto my-3">
        <div className="absolute inset-x-0 top-0 bottom-4 rounded-t-full bg-primary/10 overflow-hidden border border-primary/20">
          {isSavings ? (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: `${fillPct}%` }}
              transition={{ duration: 1.2, ease: 'easeOut' }}
              className="absolute bottom-0 left-0 right-0"
              style={{ background: `linear-gradient(to top, ${fillColor}, ${fillColor}88)` }}
            />
          ) : (
            <motion.div
              initial={{ height: '100%' }}
              animate={{ height: `${fillPct}%` }}
              transition={{ duration: 1.2, ease: 'easeOut' }}
              className="absolute top-0 left-0 right-0"
              style={{ background: `linear-gradient(to bottom, ${fillColor}, ${fillColor}88)` }}
            />
          )}
        </div>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full border border-primary/20 flex items-center justify-center"
          style={{ background: fillColor }}>
          <DollarSign className="w-5 h-5 text-white" />
        </div>
      </div>

      <div className="text-center space-y-1 mt-2">
        <p className="text-sm font-semibold text-text-main">${currentNum.toLocaleString()}</p>
        <p className="text-[10px] text-text-muted">
          {isSavings ? `of $${targetNum.toLocaleString()} goal` : `started at $${targetNum.toLocaleString()}`}
        </p>
      </div>
    </div>
  );
};

/* ─── Financial Update Modal ─── */
const FinancialUpdateModal = ({ goalType, currentValue, onSave, onClose }) => {
  const [amount, setAmount] = useState(currentValue?.toString() || '');
  const [saving, setSaving] = useState(false);
  const isSavings = goalType === 'Build my savings';

  const handleSave = async () => {
    if (!amount) return;
    setSaving(true);
    await onSave(parseFloat(amount));
    setSaving(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-card rounded-arch-xl p-6 w-full max-w-sm"
        onClick={e => e.stopPropagation()}
        data-testid="financial-update-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-text-main">
            Update {isSavings ? 'Current Savings' : 'Current Debt'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-primary/10 rounded-full" data-testid="close-financial-modal">
            <X className="w-4 h-4 text-text-muted" />
          </button>
        </div>
        <div className="mb-4">
          <label className="text-xs text-text-muted mb-1 block">
            {isSavings ? 'How much have you saved so far?' : 'What is your current remaining debt?'}
          </label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="number"
              min="0"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              className="w-full pl-9 pr-4 py-3 bg-white/70 dark:bg-white dark:text-gray-800 rounded-arch border border-primary/20 focus:ring-2 focus:ring-primary"
              placeholder="0"
              autoFocus
              data-testid="financial-amount-input"
            />
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !amount}
          className="w-full py-2.5 bg-primary text-white rounded-full text-sm font-medium btn-hover flex items-center justify-center gap-2 disabled:opacity-40"
          data-testid="save-financial-btn"
        >
          {saving ? 'Saving...' : <><Save className="w-4 h-4" /> Update Amount</>}
        </button>
      </motion.div>
    </div>
  );
};

/* ─── Salah Tracker ─── */
const SalahTracker = ({ userId, gender, selectedDate, onStreakComplete, onDataChanged, onStreakChange }) => {
  const todayStr = new Date().toISOString().split('T')[0];
  const activeDate = selectedDate || todayStr;
  const isToday = activeDate === todayStr;

  const [log, setLog] = useState({ fajr: false, dhuhr: false, asr: false, maghrib: false, isha: false, is_excused: false });
  const [streak, setStreak] = useState({ streak: 0, goal: 21, completed: false });
  const [updating, setUpdating] = useState(null);

  const fetchData = useCallback(async () => {
    if (!userId) return;
    try {
      const fetchLog = isToday
        ? api.getSalahToday(userId)
        : api.getSalahDate(userId, activeDate);
      const [logRes, streakRes] = await Promise.all([
        fetchLog,
        api.getSalahStreak(userId),
      ]);
      setLog(logRes.data);
      setStreak(streakRes.data);
    } catch (e) { /* table may not exist yet */ }
  }, [userId, activeDate, isToday]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const togglePrayer = async (prayer) => {
    if (log.is_excused) return;
    setUpdating(prayer);
    const newVal = !log[prayer];
    setLog(prev => ({ ...prev, [prayer]: newVal }));

    try {
      const logFn = isToday
        ? api.logSalah(userId, { [prayer]: newVal })
        : api.logSalahDate(userId, activeDate, { [prayer]: newVal });
      const res = await logFn;
      setLog(res.data);
      onDataChanged?.();

      const d = res.data;
      if (d.fajr && d.dhuhr && d.asr && d.maghrib && d.isha) {
        const streakRes = await api.getSalahStreak(userId);
        setStreak(streakRes.data);
        onStreakChange?.(streakRes.data.streak);
        if (streakRes.data.streak >= 21) {
          onStreakComplete?.();
        }
      }
    } catch (e) { /* revert on error */ }
    setUpdating(null);
  };

  const toggleExcused = async () => {
    const newVal = !log.is_excused;
    setLog(prev => ({ ...prev, is_excused: newVal }));
    try {
      const logFn = isToday
        ? api.logSalah(userId, { is_excused: newVal })
        : api.logSalahDate(userId, activeDate, { is_excused: newVal });
      const res = await logFn;
      setLog(res.data);
      onDataChanged?.();
      const streakRes = await api.getSalahStreak(userId);
      setStreak(streakRes.data);
      onStreakChange?.(streakRes.data.streak);
    } catch (e) { /* ignore */ }
  };

  const allComplete = log.fajr && log.dhuhr && log.asr && log.maghrib && log.isha;
  const streakPct = Math.min(100, Math.round((streak.streak / streak.goal) * 100));
  const isSister = gender === 'sister';

  const dateLabel = isToday ? 'Today' : new Date(activeDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <div data-testid="salah-tracker">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-text-main flex items-center gap-2">
          <Star className="w-4 h-4 text-amber-500" />
          Salah Tracker
        </h3>
        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
          isToday ? 'bg-primary/10 text-primary' : 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
        }`} data-testid="salah-date-label">
          {dateLabel}
        </span>
      </div>

      {/* Excused toggle for sisters */}
      {isSister && (
        <div className="flex items-center justify-between mb-4 p-2.5 bg-pink-50 dark:bg-pink-900/10 rounded-arch-lg border border-pink-200/30">
          <span className="text-[11px] text-pink-700 dark:text-pink-300 font-medium">Menstrual Cycle (excused)</span>
          <button
            type="button"
            onClick={toggleExcused}
            className={`relative w-11 h-6 rounded-full transition-colors ${log.is_excused ? 'bg-pink-400' : 'bg-gray-300 dark:bg-gray-600'}`}
            data-testid="excused-toggle"
          >
            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ${log.is_excused ? 'left-[22px]' : 'left-0.5'}`} />
          </button>
        </div>
      )}

      {log.is_excused ? (
        <div className="text-center py-4 px-3 bg-pink-50/50 dark:bg-pink-900/5 rounded-arch-lg">
          <p className="text-xs text-pink-600 dark:text-pink-300 font-medium">{isToday ? 'Today' : dateLabel} is excused</p>
          <p className="text-[10px] text-text-muted mt-1">Your streak is paused, not reset</p>
        </div>
      ) : (
        <div className="grid grid-cols-5 gap-1.5">
          {PRAYER_NAMES.map(p => (
            <button
              key={p}
              onClick={() => togglePrayer(p)}
              disabled={updating === p}
              className="flex flex-col items-center gap-1 group"
              data-testid={`prayer-${p}`}
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                log[p]
                  ? 'bg-amber-400 shadow-md shadow-amber-300/30 scale-110'
                  : 'bg-primary/5 hover:bg-amber-100 dark:hover:bg-amber-900/20'
              }`}>
                <Star className={`w-4 h-4 transition-all ${
                  log[p] ? 'text-white fill-white' : 'text-amber-400/50'
                }`} />
              </div>
              <span className={`text-[9px] leading-tight ${log[p] ? 'text-amber-600 font-semibold' : 'text-text-muted'}`}>
                {PRAYER_LABELS[p]}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Completion status */}
      {!log.is_excused && allComplete && (
        <div className="mt-3 text-center">
          <p className="text-[11px] text-green-600 dark:text-green-400 font-medium flex items-center justify-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" /> All prayers complete{isToday ? ' today' : ` on ${dateLabel}`}!
          </p>
        </div>
      )}

      {/* 21-day streak bar */}
      <div className="mt-4 pt-3 border-t border-white/15">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] text-text-muted font-medium">21-Day Salah Habit Challenge</span>
          <span className="text-[10px] font-bold text-primary">{streak.streak}/{streak.goal}</span>
        </div>
        <div className="w-full h-2 bg-primary/10 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${streakPct}%` }}
            transition={{ duration: 0.8 }}
            className="h-full rounded-full"
            style={{ background: 'linear-gradient(90deg, #f59e0b, #22c55e)' }}
          />
        </div>
        {streak.completed && (
          <p className="text-[10px] text-green-600 dark:text-green-400 font-medium mt-1.5 text-center">
            Habit built! MashaAllah!
          </p>
        )}
      </div>
    </div>
  );
};

/* ─── Mini Prayer Calendar ─── */
const MiniPrayerCalendar = ({ userId, selectedDate, onSelectDate }) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [logs, setLogs] = useState([]);
  const [earliestDate, setEarliestDate] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Expose a refresh method via the onSelectDate callback pattern
  const refreshLogs = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  useEffect(() => {
    if (!userId) return;
    api.getSalahMonth(userId, year, month).then(r => setLogs(r.data || [])).catch(() => {});
  }, [userId, year, month, refreshKey]);

  useEffect(() => {
    if (!userId) return;
    api.getSalahEarliest(userId).then(r => {
      if (r.data?.earliest_date) setEarliestDate(r.data.earliest_date);
    }).catch(() => {});
  }, [userId]);

  const monthName = new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const todayDay = now.getDate();
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;
  const todayStr = now.toISOString().split('T')[0];

  const logMap = {};
  for (const log of logs) {
    const day = parseInt(log.date.split('-')[2], 10);
    logMap[day] = log;
  }

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else { setMonth(m => m - 1); }
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else { setMonth(m => m + 1); }
  };

  const handleDayClick = (day) => {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    // Future days not clickable
    if (dateStr > todayStr) return;

    // Before earliest tracking date — not clickable (unless no earliest, allow today only)
    if (earliestDate && dateStr < earliestDate) return;

    onSelectDate?.(dateStr, refreshLogs);
  };

  // Determine which date is "selected" from the selectedDate prop
  const selectedDay = (() => {
    if (!selectedDate) return null;
    const parts = selectedDate.split('-');
    if (parseInt(parts[0], 10) === year && parseInt(parts[1], 10) === month) {
      return parseInt(parts[2], 10);
    }
    return null;
  })();

  return (
    <div data-testid="mini-prayer-calendar">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-text-main flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-primary" />
          Prayer Calendar
        </h3>
      </div>

      <div className="flex items-center justify-between mb-2">
        <button onClick={prevMonth} className="p-1 hover:bg-primary/10 rounded-full" data-testid="calendar-prev">
          <ChevronLeft className="w-3.5 h-3.5 text-text-muted" />
        </button>
        <span className="text-xs font-medium text-text-main">{monthName}</span>
        <button onClick={nextMonth} className="p-1 hover:bg-primary/10 rounded-full" data-testid="calendar-next">
          <ChevronRight className="w-3.5 h-3.5 text-text-muted" />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div key={i} className="text-center text-[8px] text-text-muted font-medium py-0.5">{d}</div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-0.5">
        {Array.from({ length: firstDay }, (_, i) => (
          <div key={`e-${i}`} className="aspect-square" />
        ))}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;
          const log = logMap[day];
          const isExcused = log?.is_excused;
          const allDone = log && log.fajr && log.dhuhr && log.asr && log.maghrib && log.isha;
          const isToday = isCurrentMonth && day === todayDay;
          const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const isFuture = dateStr > todayStr;
          const isBeforeTracking = earliestDate && dateStr < earliestDate;
          const isClickable = !isFuture && !isBeforeTracking;
          const isSelected = day === selectedDay;

          return (
            <button
              key={day}
              onClick={() => isClickable && handleDayClick(day)}
              disabled={!isClickable}
              className={`aspect-square rounded-full flex items-center justify-center relative transition-all ${
                isSelected ? 'bg-primary/20 ring-1.5 ring-primary' :
                isToday ? 'ring-1 ring-primary/50' : ''
              } ${isClickable ? 'cursor-pointer hover:bg-primary/10' : 'cursor-default opacity-50'}`}
              data-testid={`calendar-day-${day}`}
            >
              <span className={`text-[9px] ${isSelected ? 'text-primary font-bold' : 'text-text-muted'}`}>{day}</span>
              {allDone && !isExcused && (
                <Star className="absolute w-2.5 h-2.5 text-amber-400 fill-amber-400 -top-0.5 -right-0.5" />
              )}
              {isExcused && (
                <div className="absolute w-2 h-2 rounded-full bg-pink-300 dark:bg-pink-400/60 -top-0.5 -right-0.5" />
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 mt-2 pt-2 border-t border-white/10">
        <div className="flex items-center gap-1">
          <Star className="w-2.5 h-2.5 text-amber-400 fill-amber-400" />
          <span className="text-[8px] text-text-muted">All prayers</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-pink-300" />
          <span className="text-[8px] text-text-muted">Excused</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-primary/30 ring-1 ring-primary" />
          <span className="text-[8px] text-text-muted">Selected</span>
        </div>
      </div>
    </div>
  );
};

/* ─── Dynamic Goal Card ─── */
const GoalCard = ({ icon: Icon, title, value, subtitle, color, onEdit }) => (
  <div className="p-4 bg-white/30 dark:bg-white/5 rounded-arch-lg relative group" data-testid={`goal-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: `${color}15` }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <p className="font-medium text-sm text-text-main">{title}</p>
    {value ? (
      <p className="text-xs text-primary mt-1 font-medium">{value}</p>
    ) : (
      <p className="text-[11px] text-text-muted mt-1">{subtitle}</p>
    )}
    {onEdit && (
      <button onClick={onEdit}
        className="absolute top-3 right-3 p-1.5 rounded-full opacity-0 group-hover:opacity-100 hover:bg-primary/10 transition-all"
        data-testid={`edit-goal-${title.toLowerCase().replace(/\s/g, '-')}`}>
        <Pencil className="w-3 h-3 text-text-muted" />
      </button>
    )}
  </div>
);

/* ═══════════════════════════════════════════════
   DASHBOARD PAGE
   ═══════════════════════════════════════════════ */
const DashboardPage = ({ onBack, onNavigate }) => {
  const { profile } = useAuth();
  const mentorSignupEnabled = useMentorSignupEnabled();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFinancialModal, setShowFinancialModal] = useState(false);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarRefreshFn, setCalendarRefreshFn] = useState(null);
  const [salahStreak, setSalahStreak] = useState(0);

  useEffect(() => {
    if (!profile?.id) return;
    const fetchData = async () => {
      try {
        // Fire badge check in background — don't block dashboard load
        api.checkAndAwardBadges(profile.id).catch(() => {});
        const res = await api.getDashboard(profile.id);
        setData(res.data);
        setSalahStreak(res.data?.streaks?.salah || 0);
      } catch (err) {
        console.error('Error fetching dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [profile?.id]);

  const handleFinancialUpdate = async (newAmount) => {
    try {
      const logRes = await api.logFinancialProgress(profile.id, { current_amount: newAmount });
      const res = await api.getDashboard(profile.id);
      setData(res.data);

      if (logRes.data?.milestone === 'debt_paid_off') {
        const dollarShape = confetti.shapeFromText({ text: '$', scalar: 2 });
        const fire = (originX) => confetti({
          particleCount: 60,
          spread: 70,
          origin: { x: originX, y: 0.5 },
          colors: ['#22c55e', '#16a34a', '#4ade80', '#86efac', '#ffd700'],
          shapes: [dollarShape],
          scalar: 2,
        });
        fire(0.3);
        setTimeout(() => fire(0.7), 200);
        setTimeout(() => fire(0.5), 400);
      }
    } catch (e) {
      console.error('Failed to update financial progress:', e);
    }
  };

  const handleStreakComplete = () => {
    confetti({
      particleCount: 200,
      spread: 100,
      origin: { y: 0.4 },
      colors: ['#f59e0b', '#22c55e', '#4AADA8', '#A99CC8', '#FFD700'],
    });
  };

  const joinDate = data?.user?.created_at
    ? new Date(data.user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : 'Recently';

  if (loading) {
    return (
      <div className="max-w-[1200px] mx-auto p-4 lg:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-primary/10 rounded" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-3 h-96 bg-primary/5 rounded-arch-xl" />
            <div className="lg:col-span-6 space-y-6">
              <div className="h-48 bg-primary/5 rounded-arch-xl" />
              <div className="h-40 bg-primary/5 rounded-arch-xl" />
            </div>
            <div className="lg:col-span-3 h-72 bg-primary/5 rounded-arch-xl" />
          </div>
        </div>
      </div>
    );
  }

  const stats = data?.stats || {};
  const onboarding = data?.onboarding;
  const onboardingCompleted = data?.user?.onboarding_completed;
  const streaks = data?.streaks || { salah: 0, engagement: 0, reflection: 0 };

  return (
    <div className="max-w-[1200px] mx-auto p-4 lg:p-8 pb-24">
      {/* Header */}
      <motion.div {...fadeUp} className="flex items-center gap-3 mb-8">
        <button onClick={onBack} className="p-2 hover:bg-primary/10 rounded-full transition-colors" data-testid="dashboard-back-btn">
          <ChevronLeft className="w-6 h-6 text-primary" />
        </button>
        <h1 className="font-heading text-2xl font-semibold text-primary">My Dashboard</h1>
      </motion.div>

      {/* Sponsored slot */}
      <div className="mb-6"><AdSlot placement="dashboard_sidebar" /></div>

      {/* Onboarding CTA Banner */}
      {!onboardingCompleted && (
        <motion.div {...fadeUp} transition={{ delay: 0.02 }}
          className="mb-6 p-5 rounded-arch-xl border border-primary/20 flex flex-col sm:flex-row items-center justify-between gap-4"
          style={{ background: 'linear-gradient(135deg, rgba(74,173,168,0.08), rgba(169,156,200,0.08))' }}
          data-testid="onboarding-cta-banner"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-medium text-sm text-text-main">Personalize your journey</p>
              <p className="text-xs text-text-muted">Complete onboarding to unlock your goals and financial tracker</p>
            </div>
          </div>
          <button
            onClick={() => onNavigate?.('onboarding')}
            className="px-5 py-2 bg-primary text-white rounded-full text-sm font-medium btn-hover flex items-center gap-2 flex-shrink-0"
            data-testid="start-onboarding-btn"
          >
            Start Now <ArrowRight className="w-4 h-4" />
          </button>
        </motion.div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* ═══════ LEFT COLUMN ═══════ */}
        <div className="lg:col-span-3 space-y-6">

          {/* Profile Card */}
          <motion.div {...fadeUp} transition={{ delay: 0.05 }}
            className="glass-card rounded-arch-xl p-6 text-center"
            data-testid="dashboard-profile-card"
          >
            <div className="relative inline-block">
              <img
                src={data?.user?.avatar_url || defaultAvatar(data?.user?.gender)}
                alt={data?.user?.display_name}
                className="w-28 h-28 rounded-full object-cover border-4 border-primary shadow-lg mx-auto"
              />
              <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-[10px] font-bold shadow">
                {stats.marriage_readiness_pct || 0}%
              </div>
            </div>
            <h2 className="font-heading text-lg font-semibold text-text-main mt-4 cursor-pointer hover:text-primary transition-colors"
              onClick={() => onNavigate?.(`user:${data?.user?.display_name}`)}
              data-testid="dashboard-username-link">
              {data?.user?.display_name || 'User'}
            </h2>
            <p className="text-xs text-text-muted mt-1">Joined {joinDate}</p>
            {onboardingCompleted && (
              <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-[11px] font-medium"
                data-testid="onboarding-complete-badge">
                <CheckCircle2 className="w-3.5 h-3.5" /> Onboarding Complete
              </div>
            )}
          </motion.div>

          {/* Marriage Readiness */}
          <motion.div {...fadeUp} transition={{ delay: 0.1 }}
            className="glass-card rounded-arch-xl p-5"
            data-testid="dashboard-level"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-text-main">Marriage Readiness</h3>
              <span className="text-lg font-bold text-primary">{stats.marriage_readiness_pct || 0}%</span>
            </div>
            <div className="w-full h-3 bg-primary/10 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${stats.marriage_readiness_pct || 0}%` }}
                transition={{ duration: 1, ease: 'easeOut' }}
                className="h-full rounded-full"
                style={{ background: 'linear-gradient(90deg, #4AADA8, #A99CC8)' }}
              />
            </div>
            <p className="text-[10px] text-text-muted mt-2 italic">
              May Allah bless and guide you on your journey
            </p>
          </motion.div>

          {/* Badges Earned */}
          <motion.div {...fadeUp} transition={{ delay: 0.15 }}
            className="glass-card rounded-arch-xl p-5"
            data-testid="dashboard-badges"
          >
            <button onClick={() => onNavigate?.('badges')}
              className="text-sm font-medium text-text-main mb-4 flex items-center gap-2 hover:text-primary transition-colors w-full text-left"
              data-testid="badges-title-link">
              <Star className="w-4 h-4 text-primary" />
              Badges Earned
            </button>
            <div className="grid grid-cols-3 gap-3">
              {(data?.recent_badges || []).length > 0 ? (
                data.recent_badges.slice(0, 6).map((rb, i) => {
                  const BadgeIcon = badgeIconMap[rb.badge?.icon] || Star;
                  return (
                    <div key={i} className="flex flex-col items-center gap-1">
                      <div className="w-12 h-12 rounded-full bg-primary/15 border-2 border-primary/30 flex items-center justify-center">
                        <BadgeIcon className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-[9px] text-text-main text-center leading-tight font-medium">{rb.badge?.name}</span>
                    </div>
                  );
                })
              ) : (
                [...Array(3)].map((_, i) => (
                  <div key={i} className="flex flex-col items-center gap-1">
                    <div className="w-12 h-12 rounded-full bg-primary/5 border-2 border-dashed border-primary/20 flex items-center justify-center">
                      <Lock className="w-4 h-4 text-primary/30" />
                    </div>
                    <span className="text-[9px] text-text-muted text-center leading-tight">Locked</span>
                  </div>
                ))
              )}
            </div>
          </motion.div>

          {/* Financial Thermometer — moved to left column, below badges */}
          {onboardingCompleted && onboarding?.financial_goal_type && (
            <motion.div {...fadeUp} transition={{ delay: 0.18 }}
              className="glass-card rounded-arch-xl p-5"
            >
              {onboarding.financial_current_amount != null && onboarding.financial_target_amount != null ? (
                <FinancialThermometer
                  goalType={onboarding.financial_goal_type}
                  current={onboarding.financial_current_amount}
                  target={onboarding.financial_target_amount}
                  onEdit={() => setShowFinancialModal(true)}
                />
              ) : (
                <div className="text-center py-2" data-testid="financial-setup-prompt">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(169,156,200,0.15)' }}>
                    <TrendingUp className="w-5 h-5 text-secondary" aria-hidden="true" />
                  </div>
                  <p className="font-heading font-semibold text-text-main text-sm mb-1">Set up your financial goal</p>
                  <p className="text-xs text-text-muted mb-4 leading-relaxed">
                    Track your {onboarding.financial_goal_type === 'Build my savings' ? 'savings progress' : 'debt payoff journey'} toward a halal household.
                  </p>
                  <button
                    onClick={() => onNavigate?.('onboarding')}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium text-primary-foreground btn-hover"
                    style={{ background: 'linear-gradient(135deg, #4AADA8, #A99CC8)' }}
                    data-testid="financial-setup-btn"
                  >
                    Set up now <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </div>

        {/* ═══════ CENTER COLUMN ═══════ */}
        <div className="lg:col-span-6 space-y-6">

          {/* Spaces Progress — intentionally hidden from UI per product decision.
              Component logic and underlying data remain intact (see `spaces` above,
              SpacePage.js, routes/spaces.py); just not rendered. */}

          {/* Recent Activity — real data */}
          <motion.div {...fadeUp} transition={{ delay: 0.15 }}
            className="glass-card rounded-arch-xl p-6"
            data-testid="dashboard-activity"
          >
            <h3 className="text-sm font-medium text-text-main mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" />
              Recent Activity
            </h3>
            <div className="space-y-3">
              {(data?.recent_activity || []).length > 0 ? (
                <>
                  {(showAllActivity ? data.recent_activity : data.recent_activity.slice(0, 3)).map((item, i) => {
                    // Pick icon based on activity type + description
                    let ActivityIcon = Star;
                    if (item.activity_type === 'salah_full_day') ActivityIcon = CheckCircle2;
                    else if (item.activity_type === 'challenge_accepted') ActivityIcon = Sparkles;
                    else if (item.activity_type === 'space_joined') ActivityIcon = Heart;
                    else if (item.activity_type === 'lesson_completed') ActivityIcon = BookOpen;
                    else if (item.activity_type === 'debt_paid_off') ActivityIcon = DollarSign;
                    else if (item.activity_type === 'badge_earned') {
                      const desc = (item.description || '').toLowerCase();
                      if (desc.includes('ramadan')) ActivityIcon = Moon;
                      else if (desc.includes('graduate')) ActivityIcon = GraduationCap;
                      else if (desc.includes('shahadah') || desc.includes('spiritual')) ActivityIcon = BookOpen;
                      else if (desc.includes('salah') || desc.includes('champion')) ActivityIcon = CheckCircle2;
                      else if (desc.includes('encourager')) ActivityIcon = Heart;
                      else if (desc.includes('contributor') || desc.includes('consistent')) ActivityIcon = Flame;
                      else if (desc.includes('post') || desc.includes('comment')) ActivityIcon = MessageCircle;
                      else if (desc.includes('community') || desc.includes('organizer')) ActivityIcon = Users;
                      else if (desc.includes('marriage ready')) ActivityIcon = Crown;
                    }
                    const timeAgo = item.created_at ? (() => {
                      const diff = Date.now() - new Date(item.created_at).getTime();
                      const mins = Math.floor(diff / 60000);
                      if (mins < 60) return `${mins}m ago`;
                      const hrs = Math.floor(mins / 60);
                      if (hrs < 24) return `${hrs}h ago`;
                      return `${Math.floor(hrs / 24)}d ago`;
                    })() : '';
                    return (
                      <div key={i} className="flex items-center gap-3 p-3 bg-white/20 dark:bg-white/5 rounded-arch-lg">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <ActivityIcon className="w-4 h-4 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-text-main truncate">{item.description}</p>
                          <p className="text-[10px] text-text-muted">{timeAgo}</p>
                        </div>
                      </div>
                    );
                  })}
                  {data.recent_activity.length > 3 && (
                    <button onClick={() => setShowAllActivity(!showAllActivity)}
                      className="w-full text-center text-xs text-primary hover:underline py-1"
                      data-testid="activity-toggle">
                      {showAllActivity ? 'Show less' : `Show ${data.recent_activity.length - 3} more`}
                    </button>
                  )}
                </>
              ) : (
                <div className="p-4 text-center">
                  <p className="text-xs text-text-muted">No activity yet. Start learning and engaging!</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Dynamic Goal Cards */}
          <motion.div {...fadeUp} transition={{ delay: 0.2 }}
            className="glass-card rounded-arch-xl p-6"
            data-testid="dashboard-goals"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-text-main flex items-center gap-2">
                <Target className="w-4 h-4 text-primary" />
                My Goals
              </h3>
              {onboardingCompleted && (
                <button
                  onClick={() => onNavigate?.('onboarding')}
                  className="text-[11px] text-primary hover:underline flex items-center gap-1"
                  data-testid="edit-all-goals-btn"
                >
                  <Pencil className="w-3 h-3" /> Edit Answers
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <GoalCard icon={CalendarDays} title="Marriage Timeline" value={onboarding?.marriage_timeline} subtitle="Complete onboarding to set" color="#4AADA8" onEdit={onboardingCompleted ? () => onNavigate?.('onboarding') : undefined} />
              <GoalCard icon={TrendingUp} title="Financial Goal" value={onboarding?.financial_goal_type} subtitle="Complete onboarding to set" color="#A99CC8" onEdit={onboardingCompleted ? () => onNavigate?.('onboarding') : undefined} />
              <GoalCard icon={Sparkles} title="Growth Focus" value={growthFocusLabel(onboarding?.growth_focus)} subtitle="Complete onboarding to set" color="#6BA9D4" onEdit={onboardingCompleted ? () => onNavigate?.('onboarding') : undefined} />
            </div>
          </motion.div>

          {/* Connect with a Mentor */}
          <motion.div {...fadeUp} transition={{ delay: 0.25 }}
            className="glass-card rounded-arch-xl p-6"
            data-testid="dashboard-mentors"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-text-main flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" aria-hidden="true" />
                Connect with a Mentor
                {mentorSignupEnabled === false && <span className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ background: 'rgba(169,156,200,0.18)', color: '#7d6dab' }}>Coming soon</span>}
              </h3>
            </div>
            <p className="text-xs text-text-muted mb-4 leading-relaxed">
              Get personalised guidance from experienced community mentors on your journey toward marriage readiness.
            </p>
            {mentorSignupEnabled === null ? null : mentorSignupEnabled === false ? (
              <p className="text-xs text-center py-2.5 rounded-arch" style={{ background: 'rgba(169,156,200,0.12)', color: 'var(--accent-lavender)', border: '1px solid rgba(169,156,200,0.2)' }} data-testid="dashboard-mentors-coming-soon">
                We are welcoming our first mentors. Check back soon.
              </p>
            ) : profile?.is_premium ? (
              <button
                onClick={() => onNavigate?.('mentors')}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-arch text-sm font-medium transition-colors"
                style={{ background: 'linear-gradient(90deg, rgba(74,173,168,0.12), rgba(169,156,200,0.12))', color: 'var(--primary)', border: '1px solid rgba(74,173,168,0.2)' }}
                data-testid="dashboard-browse-mentors-btn"
              >
                Browse Mentors
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </button>
            ) : (
              <button
                onClick={() => onNavigate?.('premium')}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-arch text-sm font-medium transition-colors"
                style={{ background: 'rgba(169,156,200,0.12)', color: 'var(--accent-lavender)', border: '1px solid rgba(169,156,200,0.2)' }}
                data-testid="dashboard-mentors-upgrade-btn"
              >
                <Sparkles className="w-4 h-4" aria-hidden="true" />
                Upgrade to unlock
              </button>
            )}
          </motion.div>
        </div>

        {/* ═══════ RIGHT COLUMN ═══════ */}
        <div className="lg:col-span-3 space-y-6">

          {/* Today's Challenge — real data */}
          <motion.div {...fadeUp} transition={{ delay: 0.1 }}
            className="rounded-arch-xl p-5 text-white relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #4AADA8 0%, #A99CC8 100%)' }}
            data-testid="dashboard-challenge"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-x-4 -translate-y-8" />
            <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full translate-x-2 translate-y-4" />
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4" />
                <h3 className="text-sm font-medium">Today's Challenge</h3>
              </div>
              {data?.today_challenge ? (
                <>
                  <p className="text-sm leading-relaxed opacity-95">
                    {data.today_challenge.challenge_text}
                  </p>
                  {data.challenge_accepted ? (
                    <div className="mt-4 px-4 py-2 bg-white/30 rounded-full text-xs font-medium inline-flex items-center gap-1.5"
                      data-testid="challenge-accepted-badge">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Challenge Accepted
                    </div>
                  ) : (
                    <button onClick={async () => {
                      if (!profile?.id || !data?.today_challenge?.id) return;
                      try {
                        await api.acceptChallenge(data.today_challenge.id, profile.id);
                        posthog.capture('habit_completed');
                        onNavigate?.('feed', { challengeText: data.today_challenge.challenge_text });
                      } catch { /* ignore */ }
                    }}
                      className="mt-4 px-4 py-2 bg-white/20 rounded-full text-xs font-medium hover:bg-white/30 transition-colors"
                      data-testid="challenge-accept-btn">
                      Accept Challenge
                    </button>
                  )}
                </>
              ) : (
                <p className="text-sm leading-relaxed opacity-75">No challenge today. Check back tomorrow!</p>
              )}
            </div>
          </motion.div>

          {/* Salah Tracker */}
          {onboardingCompleted && (
            <motion.div {...fadeUp} transition={{ delay: 0.12 }}
              className="glass-card rounded-arch-xl p-5"
              data-testid="dashboard-salah-tracker"
            >
              <SalahTracker
                userId={profile?.id}
                gender={data?.user?.gender}
                selectedDate={selectedDate}
                onStreakComplete={handleStreakComplete}
                onDataChanged={() => calendarRefreshFn?.()}
                onStreakChange={(val) => setSalahStreak(val)}
              />
            </motion.div>
          )}

          {/* Mini Prayer Calendar */}
          {onboardingCompleted && (
            <motion.div {...fadeUp} transition={{ delay: 0.14 }}
              className="glass-card rounded-arch-xl p-5"
            >
              <MiniPrayerCalendar
                userId={profile?.id}
                selectedDate={selectedDate}
                onSelectDate={(date, refreshFn) => {
                  const todayStr = new Date().toISOString().split('T')[0];
                  setSelectedDate(date === todayStr ? null : date);
                  setCalendarRefreshFn(() => refreshFn);
                }}
              />
            </motion.div>
          )}

          {/* My Streaks */}
          <motion.div {...fadeUp} transition={{ delay: 0.15 }}
            className="glass-card rounded-arch-xl p-5"
            data-testid="dashboard-streaks"
          >
            <h3 className="text-sm font-medium text-text-main mb-4 flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-500" />
              My Streaks
            </h3>
            <div className="space-y-0">
              {[
                { name: 'Salah Check-in', streak: salahStreak, icon: '🤲', color: '#4AADA8' },
                { name: "Today's Challenge", streak: streaks.challenge_count, icon: '✨', color: '#A99CC8', noLabel: true },
                { name: 'Engagement', streak: streaks.engagement, icon: '💬', color: '#6BA9D4' },
              ].map((s, i, arr) => (
                <div
                  key={s.name}
                  className={`grid grid-cols-[2rem_1fr_auto] items-center gap-3 py-3 ${
                    i < arr.length - 1 ? 'border-b border-current/[0.04]' : ''
                  }`}
                  data-testid={`streak-${s.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <span className="text-lg text-center">{s.icon}</span>
                  <span className="text-xs font-medium text-text-main">{s.name}</span>
                  <span className="text-sm font-bold tabular-nums" style={{ color: s.color }}>
                    {s.streak} {!s.noLabel && <span className="text-[10px] font-normal text-text-muted">days</span>}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Upcoming */}
          <motion.div {...fadeUp} transition={{ delay: 0.2 }}
            className="glass-card rounded-arch-xl p-5"
            data-testid="dashboard-upcoming"
          >
            <h3 className="text-sm font-medium text-text-main mb-3 flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-primary" />
              Upcoming
            </h3>
            <div className="space-y-3">
              <div className="p-3 bg-primary/5 rounded-arch-lg border border-dashed border-primary/20">
                <p className="text-xs text-text-muted">Community events and challenges coming soon...</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Financial Update Modal */}
      <AnimatePresence>
        {showFinancialModal && onboarding && (
          <FinancialUpdateModal
            goalType={onboarding.financial_goal_type}
            currentValue={onboarding.financial_current_amount}
            onSave={handleFinancialUpdate}
            onClose={() => setShowFinancialModal(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default DashboardPage;
